import FeatureGates from '@atlaskit/feature-gate-js-client';

import type { PluginConfiguration } from '../malleableUiPluginType';
import { parseMauiDiagramLink, type RovoConversationContext } from '../pm-plugins/detect-maui-link';
import {
	executeMauiEmbedFinalize,
	getMauiDiagramPasteView,
	type MauiEmbedFinalizeMode,
	type MauiEmbedFinalizeWorkflowArgs,
	type MauiEmbedTargetSpace,
} from '../pm-plugins/maui-diagram-paste-plugin';

export type FinalizeMauiEmbedFromIndicatorArgs = {
	contentId?: string;
	conversationContext?: RovoConversationContext;
	mode?: MauiEmbedFinalizeMode;
	originalUrl: string;
	/** When supplied, skips the probe and finalizes into the picked space. */
	targetSpace?: MauiEmbedTargetSpace;
};

export type FinalizeMauiEmbedFromIndicator = (
	args: FinalizeMauiEmbedFromIndicatorArgs,
) => Promise<void>;

export function makeFinalizeMauiEmbedFromIndicator({
	config,
}: {
	config: PluginConfiguration | undefined;
}): FinalizeMauiEmbedFromIndicator {
	return async (args: FinalizeMauiEmbedFromIndicatorArgs) => {
		if (
			args.targetSpace &&
			// eslint-disable-next-line @atlaskit/platform/use-recommended-utils -- dynamic config killswitch, not a standard feature gate
			FeatureGates.getExperimentValue<boolean>(
				'maui-native-embed-finalize-diagram-anywhere',
				'value',
				false,
			)
		) {
			return;
		}

		const diagramPaste = config?.diagramPaste;
		if (!diagramPaste) {
			return;
		}

		const { originalUrl, conversationContext, mode, targetSpace } = args;

		// Use the view that owns this embed URL.
		const view = getMauiDiagramPasteView(originalUrl);
		if (!view) {
			return;
		}
		let contentId = args.contentId;
		if (!contentId) {
			const parsed = parseMauiDiagramLink(originalUrl);
			contentId = parsed?.contentId;
		}
		if (!contentId) {
			return;
		}

		const workflowArgs: MauiEmbedFinalizeWorkflowArgs = {
			contentId,
			originalUrl,
			conversationContext,
			mode,
			targetSpace,
		};

		await executeMauiEmbedFinalize(view, diagramPaste, workflowArgs);
	};
}
